// src/api/contractsApi.js
// Contracts — DocuSign send + status. The backend is a stateless proxy: the
// ContractTemplate/ContractInstance records live in the state blob, so the
// client passes the template's content and field schema through to be rendered.
import apiClient from "./apiClient";

const BASE = "/api/contracts";

// Is DocuSign usable at all, and what is the one-time consent URL? Called
// before showing the send form so the button can be disabled with a reason
// instead of failing at the API.
export async function getContractsConfig() {
  const res = await apiClient.get(`${BASE}/config`);
  return res.data;                     // { configured, environment, consentUrl }
}

// Send an envelope. Returns { envelopeId, status, docuSignStatus }.
// Sending is slow enough to warrant its own timeout — DocuSign is doing real
// work (rendering the document, emailing the recipient), not just accepting a
// record.
//
// ⚠️ This destructures an EXPLICIT list and rebuilds the body from it, which
// makes it a whitelist: anything the caller passes that is not named here is
// dropped silently, with no error at either end. `proposalContent`,
// `fieldSchema` and `documentType` were added to the call in ContractsPanel and
// to the route's handler, but not here — so the send posted seven keys, the
// backend's mergeValues had no proposal to compute pricing from, and every send
// came back 400 "Not every field is filled — userPrice, userCount, …". Both
// files either side of this one read as correct; the fields vanished in transit.
// Anything added to the route's contract has to be added HERE too.
export async function sendContract({ templateName, content, fieldSchema, fieldValues,
                                     documentType, proposalContent, recipientEmail,
                                     recipientName, proposalId, contractTemplateId }) {
  const res = await apiClient.post(`${BASE}/send`, {
    templateName, content, fieldSchema, fieldValues,
    // The backend recomputes every price from the won proposal rather than
    // trusting figures posted by a browser — so without this the computed
    // placeholders have nothing to resolve against and the send is refused.
    documentType, proposalContent,
    recipientEmail, recipientName,
    proposalId, contractTemplateId,
  }, { timeout: 120000 });
  return res.data;
}

// The ported legal text for the three tiers. Fetched rather than bundled —
// ~27KB per tier does not belong in the frontend, and a second copy of a
// contract is how one gets edited and the other gets sent.
export async function getSeedContractContent() {
  const res = await apiClient.get(`${BASE}/seed-content`);
  return res.data?.templates || [];
}

// Render the contract to PDF WITHOUT sending it. Returns a blob URL — the last
// look at a legal document before it leaves the building.
export async function previewContract({ templateName, content, fieldValues, customerName }) {
  const res = await apiClient.post(`${BASE}/preview`,
    { templateName, content, fieldValues, customerName },
    { responseType: "blob", timeout: 120000 });
  return URL.createObjectURL(res.data);
}

// Poll one envelope. v1 is pull-only by design — no webhooks.
export async function getContractStatus(envelopeId) {
  const res = await apiClient.get(`${BASE}/status/${encodeURIComponent(envelopeId)}`);
  return res.data;
}
